'use client';

import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import Image from 'next/image';
import Link from 'next/link';
import { Tabs, TabsContent } from 'components/ui/tabs';
import { Input } from 'components/ui/input';
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from 'components/ui/form';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { z } from 'zod';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Button from 'components/ui/Button';
import { PasswordInput } from '../ui/PasswordInput';
import toast from 'react-hot-toast';
import Bowser from 'bowser';
import { registerSchema } from 'lib/schemas/registerSchema';
import { useSearchParams } from 'next/navigation';
import { useAuth } from 'lib/context/User.context';
import AuthPanel from './AuthPanel';

export default function SignUp() {
  const router = useRouter();
  const { isAuthenticated, loading: authLoading, checkAuth } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState('');
  const [storedEmail, setStoredEmail] = useState('');
  const [resendTimer, setResendTimer] = useState(60);
  const [isResendDisabled, setIsResendDisabled] = useState(true);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [inviteId, setInviteId] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'yearly'>('yearly');
  const searchParams = useSearchParams();
  const tabParam = searchParams?.get('tab');
  const [tab, setTab] = useState(tabParam || 'login');

  useEffect(() => {
    if (tabParam) setTab(tabParam);
    
    // Handle plan parameter from URL
    const planParam = searchParams?.get('plan');
    if (planParam === 'monthly' || planParam === 'yearly') {
      setSelectedPlan(planParam);
    }
  }, [tabParam, searchParams]);

  // Redirect to dashboard if user is already authenticated
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      router.push('/dashboard');
    }
  }, [isAuthenticated, authLoading, router]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const invitedId = params.get('invite_id');
    const email = params.get('email');
    setInviteId(invitedId);
    setEmail(email);
  }, []);

  const inputRefs = Array.from({ length: 6 }, () => useRef<HTMLInputElement>(null));

  useEffect(() => {
    if (showOtpModal) {
      setIsResendDisabled(true);
      setResendTimer(60); // Reset timer

      const interval = setInterval(() => {
        setResendTimer((prev) => {
          if (prev === 1) {
            clearInterval(interval);
            setIsResendDisabled(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(interval); // Clean up when modal unmounts
    }
  }, [showOtpModal]);

  const handleSendOtp = async () => {
    if (isEmailVerified) {
      toast.success('Email already verified');
      return;
    }

    const emailValue = registerForm.getValues('email');
    if (!emailValue) {
      toast.error('Please enter your email first');
      return;
    }

    const emailToSend = emailValue.toLowerCase();

    if (!/\S+@\S+\.\S+/.test(emailToSend)) {
      toast.error('Please enter a valid email address');
      return;
    }

    try {
      setIsSendingOtp(true);
      const res = await fetch('/api/send-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailToSend }),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setStoredEmail(emailToSend);
        setShowOtpModal(true);
        toast.success('OTP sent!');
      } else {
        toast.error(data.message || 'Failed to send OTP');
      }
    } catch (error) {
      toast.error('Something went wrong while sending OTP');
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otp.length !== 6) {
      toast.error('Please enter a 6-digit OTP');
      return;
    }

    try {
      const res = await fetch('/api/verify-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: storedEmail,
          otp: otp,
        }),
      });

      const data = await res.json();
      if (data.verified) {
        setIsEmailVerified(true);
        setShowOtpModal(false);
        setOtp('');
        toast.success('Email verified successfully!');
      } else {
        toast.error(data.message || 'Invalid OTP');
      }
    } catch (error) {
      toast.error('Something went wrong while verifying OTP');
    }
  };

  const handleResendOtp = async () => {
    if (isResending) return;

    try {
      setIsResending(true);
      const res = await fetch('/api/send-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: storedEmail }),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setResendTimer(60);
        setIsResendDisabled(true);
        toast.success('OTP resent!');
      } else {
        toast.error(data.message || 'Failed to resend OTP');
      }
    } catch (error) {
      toast.error('Something went wrong while resending OTP');
    } finally {
      setIsResending(false);
    }
  };

  const registerForm = useForm({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      username: '',
      email: '',
      password: '',
      confirmPassword: '',
    },
  });

  useEffect(() => {
    if (email && inviteId) {
      registerForm.setValue('email', email as string);
      setIsEmailVerified(true); // Since it's invite based
    }
  }, [email, inviteId]);

  const onSubmitRegister = async (values: z.infer<typeof registerSchema>) => {
    setIsLoading(true);

    try {
      // 1. Check if user already exists
      if (!inviteId) {
        const checkRes = await fetch('/api/auth/check-user', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: values.email }),
        });

        const checkData = await checkRes.json();
        if (checkData.exists) {
          toast.error('User already exists with this email.');
          return;
        }
      }

      // 2. Validate invite (if applicable)
      if (inviteId) {
        const res = await fetch(`/api/invite/${inviteId}`);
        const inviteData = await res.json();

        if (!res.ok || !inviteData) {
          toast.error('Invalid invite link or invite not found.');
          return;
        }

        if (inviteData.invited_email !== values.email) {
          toast.error('Invite does not match the provided email.');
          return;
        }

        (values as any).company_id = inviteData.company_id;
        (values as any).invite_id = inviteId;
      }

      // 3. Create Stripe checkout session for signup with payment
      const checkoutResponse = await fetch('/api/auth/signup-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: values.username,
          email: values.email,
          password: values.password,
          plan: selectedPlan,
          inviteId: inviteId,
        }),
      });

      const checkoutData = await checkoutResponse.json();

      if (!checkoutResponse.ok || !checkoutData.success) {
        toast.error(checkoutData.message || 'Failed to create checkout session');
        return;
      }

      // Redirect to Stripe checkout
      if (checkoutData.url) {
        window.location.href = checkoutData.url;
      } else {
        toast.error('No checkout URL received');
      }
    } catch (err) {
      console.error('Unexpected error during registration:', err);
      toast.error('Unexpected error during registration.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) return; // Prevent multiple characters

    const newOtp = otp.split('');
    newOtp[index] = value;
    const updatedOtp = newOtp.join('');
    setOtp(updatedOtp);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs[index + 1].current?.focus();
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, ''); // Remove non-digits
    
    if (pastedData.length === 6) {
      // If pasted data is exactly 6 digits, distribute it across all inputs
      const otpArray = pastedData.split('');
      setOtp(pastedData);
      
      // Focus the last input
      inputRefs[5].current?.focus();
    } else if (pastedData.length > 0) {
      // If pasted data is less than 6 digits, fill from the current position
      const currentIndex = inputRefs.findIndex(ref => ref.current === e.target);
      const newOtp = otp.split('');
      
      for (let i = 0; i < pastedData.length && currentIndex + i < 6; i++) {
        newOtp[currentIndex + i] = pastedData[i];
      }
      
      setOtp(newOtp.join(''));
      
      // Focus the next empty input or the last filled input
      const nextIndex = Math.min(currentIndex + pastedData.length, 5);
      inputRefs[nextIndex].current?.focus();
    }
  };

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <div data-component-id="src/components/auth/SignUp.tsx:330:6" data-component-path="src/components/auth/SignUp.tsx" data-component-line="330" data-component-file="SignUp.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20flex%20items-center%20justify-center%22%7D" className="min-h-screen flex items-center justify-center">
        <div data-component-id="src/components/auth/SignUp.tsx:331:8" data-component-path="src/components/auth/SignUp.tsx" data-component-line="331" data-component-file="SignUp.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-2%22%7D" className="flex items-center gap-2">
          <Loader2 data-component-id="src/components/auth/SignUp.tsx:332:10" data-component-path="src/components/auth/SignUp.tsx" data-component-line="332" data-component-file="SignUp.tsx" data-component-name="Loader2" data-component-content="%7B%22elementName%22%3A%22Loader2%22%2C%22className%22%3A%22w-6%20h-6%20animate-spin%22%7D" className="w-6 h-6 animate-spin" />
          <span data-component-id="src/components/auth/SignUp.tsx:333:10" data-component-path="src/components/auth/SignUp.tsx" data-component-line="333" data-component-file="SignUp.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Loading...%22%7D">Loading...</span>
        </div>
      </div>
    );
  }

  // Don't render the form if user is authenticated (will redirect)
  if (isAuthenticated) {
    return null;
  }

  return (
    <div data-component-id="src/components/auth/SignUp.tsx:345:4" data-component-path="src/components/auth/SignUp.tsx" data-component-line="345" data-component-file="SignUp.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20flex%20flex-col%20md%3Aflex-row%22%7D" className="min-h-screen flex flex-col md:flex-row">
      {/* Hero Section - Left */}
      <AuthPanel data-component-id="src/components/auth/SignUp.tsx:347:6" data-component-path="src/components/auth/SignUp.tsx" data-component-line="347" data-component-file="SignUp.tsx" data-component-name="AuthPanel" data-component-content="%7B%22elementName%22%3A%22AuthPanel%22%7D" />

      {/* Form - Right */}
      <div data-component-id="src/components/auth/SignUp.tsx:350:6" data-component-path="src/components/auth/SignUp.tsx" data-component-line="350" data-component-file="SignUp.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-full%20md%3Aw-1%2F2%20flex%20items-center%20justify-center%22%7D" className="w-full md:w-1/2 flex items-center justify-center">
        <div data-component-id="src/components/auth/SignUp.tsx:351:8" data-component-path="src/components/auth/SignUp.tsx" data-component-line="351" data-component-file="SignUp.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20w-full%20max-w-%5B560px%5D%20gap-%5B32px%5D%20p-6%20sm%3Ap-8%20md%3Ap-10%20lg%3Ap-14%20xl%3Ap-16%20%22%7D" className="flex flex-col w-full max-w-[560px] gap-[32px] p-6 sm:p-8 md:p-10 lg:p-14 xl:p-16 ">
          <div data-component-id="src/components/auth/SignUp.tsx:352:10" data-component-path="src/components/auth/SignUp.tsx" data-component-line="352" data-component-file="SignUp.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%22%7D" className="text-center">
            <h1 data-component-id="src/components/auth/SignUp.tsx:353:12" data-component-path="src/components/auth/SignUp.tsx" data-component-line="353" data-component-file="SignUp.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%2C%22className%22%3A%22text-%5B26px%5D%20sm%3Atext-3xl%20font-bold%22%2C%22textContent%22%3A%22Start%20Your%20Free%20Trial%22%7D" className="text-[26px] sm:text-3xl font-bold">Start Your Free Trial</h1>
            <p data-component-id="src/components/auth/SignUp.tsx:354:12" data-component-path="src/components/auth/SignUp.tsx" data-component-line="354" data-component-file="SignUp.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-%5B14px%5D%22%2C%22textContent%22%3A%22Choose%20your%20plan%20and%20start%20your%2014-day%20free%20trial%20with%20credit%20card%22%7D" className="text-[14px]">
              Choose your plan and start your 14-day free trial with credit card
            </p>
          </div>
          <FormProvider data-component-id="src/components/auth/SignUp.tsx:358:10" data-component-path="src/components/auth/SignUp.tsx" data-component-line="358" data-component-file="SignUp.tsx" data-component-name="FormProvider" data-component-content="%7B%22elementName%22%3A%22FormProvider%22%2C%22%5Bspread%5D%22%3A%22true%22%7D" {...registerForm}>
            <Form data-component-id="src/components/auth/SignUp.tsx:359:12" data-component-path="src/components/auth/SignUp.tsx" data-component-line="359" data-component-file="SignUp.tsx" data-component-name="Form" data-component-content="%7B%22elementName%22%3A%22Form%22%2C%22className%22%3A%22space-y-4%22%7D"
              form={registerForm}
              onSubmit={registerForm.handleSubmit(onSubmitRegister)}
              className="space-y-4"
            >
              <FormField data-component-id="src/components/auth/SignUp.tsx:364:14" data-component-path="src/components/auth/SignUp.tsx" data-component-line="364" data-component-file="SignUp.tsx" data-component-name="FormField" data-component-content="%7B%22elementName%22%3A%22FormField%22%2C%22name%22%3A%22username%22%7D"
                control={registerForm.control}
                name="username"
                render={({ field }) => (
                  <FormItem data-component-id="src/components/auth/SignUp.tsx:368:18" data-component-path="src/components/auth/SignUp.tsx" data-component-line="368" data-component-file="SignUp.tsx" data-component-name="FormItem" data-component-content="%7B%22elementName%22%3A%22FormItem%22%2C%22className%22%3A%22flex%20flex-col%22%7D" className="flex flex-col">
                    <FormLabel data-component-id="src/components/auth/SignUp.tsx:369:20" data-component-path="src/components/auth/SignUp.tsx" data-component-line="369" data-component-file="SignUp.tsx" data-component-name="FormLabel" data-component-content="%7B%22elementName%22%3A%22FormLabel%22%2C%22className%22%3A%22text-%5B%23999999%5D%20text-%5B14px%5D%22%2C%22textContent%22%3A%22Username%22%7D" className="text-[#999999] text-[14px]">Username</FormLabel>
                    <FormControl data-component-id="src/components/auth/SignUp.tsx:370:20" data-component-path="src/components/auth/SignUp.tsx" data-component-line="370" data-component-file="SignUp.tsx" data-component-name="FormControl" data-component-content="%7B%22elementName%22%3A%22FormControl%22%7D">
                      <Input data-component-id="src/components/auth/SignUp.tsx:371:22" data-component-path="src/components/auth/SignUp.tsx" data-component-line="371" data-component-file="SignUp.tsx" data-component-name="Input" data-component-content="%7B%22elementName%22%3A%22Input%22%2C%22type%22%3A%22text%22%2C%22%5Bspread%5D%22%3A%22true%22%7D" type="text" placeholder="Username" {...field} />
                    </FormControl>
                    <FormMessage data-component-id="src/components/auth/SignUp.tsx:373:20" data-component-path="src/components/auth/SignUp.tsx" data-component-line="373" data-component-file="SignUp.tsx" data-component-name="FormMessage" data-component-content="%7B%22elementName%22%3A%22FormMessage%22%7D" />
                  </FormItem>
                )}
              />
              <FormField data-component-id="src/components/auth/SignUp.tsx:377:14" data-component-path="src/components/auth/SignUp.tsx" data-component-line="377" data-component-file="SignUp.tsx" data-component-name="FormField" data-component-content="%7B%22elementName%22%3A%22FormField%22%2C%22name%22%3A%22email%22%7D"
                control={registerForm.control}
                name="email"
                render={({ field }) => (
                  <FormItem data-component-id="src/components/auth/SignUp.tsx:381:18" data-component-path="src/components/auth/SignUp.tsx" data-component-line="381" data-component-file="SignUp.tsx" data-component-name="FormItem" data-component-content="%7B%22elementName%22%3A%22FormItem%22%2C%22className%22%3A%22flex%20flex-col%22%7D" className="flex flex-col">
                    <FormLabel data-component-id="src/components/auth/SignUp.tsx:382:20" data-component-path="src/components/auth/SignUp.tsx" data-component-line="382" data-component-file="SignUp.tsx" data-component-name="FormLabel" data-component-content="%7B%22elementName%22%3A%22FormLabel%22%2C%22className%22%3A%22text-%5B%23999999%5D%20text-%5B14px%5D%22%2C%22textContent%22%3A%22Email%22%7D" className="text-[#999999] text-[14px]">Email</FormLabel>
                    <div data-component-id="src/components/auth/SignUp.tsx:383:20" data-component-path="src/components/auth/SignUp.tsx" data-component-line="383" data-component-file="SignUp.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20gap-2%22%7D" className="flex gap-2">
                      <FormControl data-component-id="src/components/auth/SignUp.tsx:384:22" data-component-path="src/components/auth/SignUp.tsx" data-component-line="384" data-component-file="SignUp.tsx" data-component-name="FormControl" data-component-content="%7B%22elementName%22%3A%22FormControl%22%7D">
                        <Input data-component-id="src/components/auth/SignUp.tsx:385:24" data-component-path="src/components/auth/SignUp.tsx" data-component-line="385" data-component-file="SignUp.tsx" data-component-name="Input" data-component-content="%7B%22elementName%22%3A%22Input%22%2C%22type%22%3A%22email%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
                          type="email"
                          placeholder="email"
                          {...field}
                          disabled={Boolean(inviteId)}
                        />
                      </FormControl>
                      <Button data-component-id="src/components/auth/SignUp.tsx:392:22" data-component-path="src/components/auth/SignUp.tsx" data-component-line="392" data-component-file="SignUp.tsx" data-component-name="Button" data-component-content="%7B%22elementName%22%3A%22Button%22%2C%22type%22%3A%22button%22%7D"
                        type="button"
                        onClick={handleSendOtp}
                        disabled={isSendingOtp || isEmailVerified}
                      >
                        {isSendingOtp ? (
                          <Loader2 data-component-id="src/components/auth/SignUp.tsx:398:26" data-component-path="src/components/auth/SignUp.tsx" data-component-line="398" data-component-file="SignUp.tsx" data-component-name="Loader2" data-component-content="%7B%22elementName%22%3A%22Loader2%22%2C%22className%22%3A%22w-4%20h-4%20animate-spin%22%7D" className="w-4 h-4 animate-spin" />
                        ) : isEmailVerified ? (
                          'Verified'
                        ) : (
                          'Verify'
                        )}
                      </Button>
                    </div>
                  </FormItem>
                )}
              />
              <FormField data-component-id="src/components/auth/SignUp.tsx:409:14" data-component-path="src/components/auth/SignUp.tsx" data-component-line="409" data-component-file="SignUp.tsx" data-component-name="FormField" data-component-content="%7B%22elementName%22%3A%22FormField%22%2C%22name%22%3A%22password%22%7D"
                control={registerForm.control}
                name="password"
                render={({ field }) => (
                  <FormItem data-component-id="src/components/auth/SignUp.tsx:413:18" data-component-path="src/components/auth/SignUp.tsx" data-component-line="413" data-component-file="SignUp.tsx" data-component-name="FormItem" data-component-content="%7B%22elementName%22%3A%22FormItem%22%2C%22className%22%3A%22flex%20flex-col%22%7D" className="flex flex-col">
                    <FormLabel data-component-id="src/components/auth/SignUp.tsx:414:20" data-component-path="src/components/auth/SignUp.tsx" data-component-line="414" data-component-file="SignUp.tsx" data-component-name="FormLabel" data-component-content="%7B%22elementName%22%3A%22FormLabel%22%2C%22className%22%3A%22text-%5B%23999999%5D%20text-%5B14px%5D%22%2C%22textContent%22%3A%22Password%22%7D" className="text-[#999999] text-[14px]">Password</FormLabel>
                    <FormControl data-component-id="src/components/auth/SignUp.tsx:415:20" data-component-path="src/components/auth/SignUp.tsx" data-component-line="415" data-component-file="SignUp.tsx" data-component-name="FormControl" data-component-content="%7B%22elementName%22%3A%22FormControl%22%7D">
                      <PasswordInput data-component-id="src/components/auth/SignUp.tsx:416:22" data-component-path="src/components/auth/SignUp.tsx" data-component-line="416" data-component-file="SignUp.tsx" data-component-name="PasswordInput" data-component-content="%7B%22elementName%22%3A%22PasswordInput%22%2C%22%5Bspread%5D%22%3A%22true%22%7D" placeholder="••••••••" {...field} />
                    </FormControl>
                    <FormMessage data-component-id="src/components/auth/SignUp.tsx:418:20" data-component-path="src/components/auth/SignUp.tsx" data-component-line="418" data-component-file="SignUp.tsx" data-component-name="FormMessage" data-component-content="%7B%22elementName%22%3A%22FormMessage%22%7D" />
                  </FormItem>
                )}
              />

              <FormField data-component-id="src/components/auth/SignUp.tsx:423:14" data-component-path="src/components/auth/SignUp.tsx" data-component-line="423" data-component-file="SignUp.tsx" data-component-name="FormField" data-component-content="%7B%22elementName%22%3A%22FormField%22%2C%22name%22%3A%22confirmPassword%22%7D"
                control={registerForm.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem data-component-id="src/components/auth/SignUp.tsx:427:18" data-component-path="src/components/auth/SignUp.tsx" data-component-line="427" data-component-file="SignUp.tsx" data-component-name="FormItem" data-component-content="%7B%22elementName%22%3A%22FormItem%22%2C%22className%22%3A%22flex%20flex-col%22%7D" className="flex flex-col">
                    <FormLabel data-component-id="src/components/auth/SignUp.tsx:428:20" data-component-path="src/components/auth/SignUp.tsx" data-component-line="428" data-component-file="SignUp.tsx" data-component-name="FormLabel" data-component-content="%7B%22elementName%22%3A%22FormLabel%22%2C%22className%22%3A%22text-%5B%23999999%5D%20text-%5B14px%5D%22%2C%22textContent%22%3A%22Confirm%20Password%22%7D" className="text-[#999999] text-[14px]">Confirm Password</FormLabel>
                    <FormControl data-component-id="src/components/auth/SignUp.tsx:429:20" data-component-path="src/components/auth/SignUp.tsx" data-component-line="429" data-component-file="SignUp.tsx" data-component-name="FormControl" data-component-content="%7B%22elementName%22%3A%22FormControl%22%7D">
                      <PasswordInput data-component-id="src/components/auth/SignUp.tsx:430:22" data-component-path="src/components/auth/SignUp.tsx" data-component-line="430" data-component-file="SignUp.tsx" data-component-name="PasswordInput" data-component-content="%7B%22elementName%22%3A%22PasswordInput%22%2C%22%5Bspread%5D%22%3A%22true%22%7D" placeholder="Re-enter your password" {...field} />
                    </FormControl>
                    <FormMessage data-component-id="src/components/auth/SignUp.tsx:432:20" data-component-path="src/components/auth/SignUp.tsx" data-component-line="432" data-component-file="SignUp.tsx" data-component-name="FormMessage" data-component-content="%7B%22elementName%22%3A%22FormMessage%22%7D" />
                  </FormItem>
                )}
              />

              {/* Plan Selection */}
              <div data-component-id="src/components/auth/SignUp.tsx:438:14" data-component-path="src/components/auth/SignUp.tsx" data-component-line="438" data-component-file="SignUp.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20gap-3%22%7D" className="flex flex-col gap-3">
                <label data-component-id="src/components/auth/SignUp.tsx:439:16" data-component-path="src/components/auth/SignUp.tsx" data-component-line="439" data-component-file="SignUp.tsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22text-%5B%23999999%5D%20text-%5B14px%5D%22%2C%22textContent%22%3A%22Choose%20Your%20Plan%22%7D" className="text-[#999999] text-[14px]">Choose Your Plan</label>
                <div data-component-id="src/components/auth/SignUp.tsx:440:16" data-component-path="src/components/auth/SignUp.tsx" data-component-line="440" data-component-file="SignUp.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-2%20gap-3%22%7D" className="grid grid-cols-2 gap-3">
                  <button data-component-id="src/components/auth/SignUp.tsx:441:18" data-component-path="src/components/auth/SignUp.tsx" data-component-line="441" data-component-file="SignUp.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%7D"
                    type="button"
                    onClick={() => setSelectedPlan('yearly')}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      selectedPlan === 'yearly'
                        ? 'border-[#295fd8] bg-[#295fd8]/10'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <div data-component-id="src/components/auth/SignUp.tsx:450:20" data-component-path="src/components/auth/SignUp.tsx" data-component-line="450" data-component-file="SignUp.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-left%22%7D" className="text-left">
                      <div data-component-id="src/components/auth/SignUp.tsx:451:22" data-component-path="src/components/auth/SignUp.tsx" data-component-line="451" data-component-file="SignUp.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22font-semibold%20text-%5B20px%5D%22%2C%22textContent%22%3A%22Yearly%22%7D" className="font-semibold text-[20px]">Yearly</div>
                      <div data-component-id="src/components/auth/SignUp.tsx:452:22" data-component-path="src/components/auth/SignUp.tsx" data-component-line="452" data-component-file="SignUp.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-%5B15px%5D%20text-gray-600%22%2C%22textContent%22%3A%22%24325%2Fmonth%22%7D" className="text-[15px] text-gray-600">$325/month</div>
                    </div>
                  </button>
                  <button data-component-id="src/components/auth/SignUp.tsx:455:18" data-component-path="src/components/auth/SignUp.tsx" data-component-line="455" data-component-file="SignUp.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%7D"
                    type="button"
                    onClick={() => setSelectedPlan('monthly')}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      selectedPlan === 'monthly'
                        ? 'border-[#295fd8] bg-[#295fd8]/10'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <div data-component-id="src/components/auth/SignUp.tsx:464:20" data-component-path="src/components/auth/SignUp.tsx" data-component-line="464" data-component-file="SignUp.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-left%22%7D" className="text-left">
                      <div data-component-id="src/components/auth/SignUp.tsx:465:22" data-component-path="src/components/auth/SignUp.tsx" data-component-line="465" data-component-file="SignUp.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22font-semibold%20text-%5B20px%5D%22%2C%22textContent%22%3A%22Monthly%22%7D" className="font-semibold text-[20px]">Monthly</div>
                      <div data-component-id="src/components/auth/SignUp.tsx:466:22" data-component-path="src/components/auth/SignUp.tsx" data-component-line="466" data-component-file="SignUp.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-%5B15px%5D%20text-gray-600%22%2C%22textContent%22%3A%22%24395%2Fmonth%22%7D" className="text-[15px] text-gray-600">$395/month</div>
                    </div>
                  </button>
                </div>
              </div>


              <Button data-component-id="src/components/auth/SignUp.tsx:473:14" data-component-path="src/components/auth/SignUp.tsx" data-component-line="473" data-component-file="SignUp.tsx" data-component-name="Button" data-component-content="%7B%22elementName%22%3A%22Button%22%2C%22type%22%3A%22submit%22%2C%22className%22%3A%22w-full%20bg-gray-400%20text-%5B14px%5D%22%7D"
                type="submit"
                className="w-full bg-gray-400 text-[14px]"
                disabled={!isEmailVerified || isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 data-component-id="src/components/auth/SignUp.tsx:480:20" data-component-path="src/components/auth/SignUp.tsx" data-component-line="480" data-component-file="SignUp.tsx" data-component-name="Loader2" data-component-content="%7B%22elementName%22%3A%22Loader2%22%2C%22className%22%3A%22mr-2%20h-4%20w-4%20animate-spin%22%7D" className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  'Continue to Payment'
                )}
              </Button>
            </Form>
          </FormProvider>

          <div data-component-id="src/components/auth/SignUp.tsx:490:10" data-component-path="src/components/auth/SignUp.tsx" data-component-line="490" data-component-file="SignUp.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20justify-center%20items-center%20gap-%5B3px%5D%22%7D" className="flex justify-center items-center gap-[3px]">
            <p data-component-id="src/components/auth/SignUp.tsx:491:12" data-component-path="src/components/auth/SignUp.tsx" data-component-line="491" data-component-file="SignUp.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22m-0%20p-0%20text-gray-600%20font-bold%20text-%5B14px%5D%22%2C%22textContent%22%3A%22Already%20have%20an%20account%3F%22%7D" className="m-0 p-0 text-gray-600 font-bold text-[14px]">Already have an account?</p>
            <Link data-component-id="src/components/auth/SignUp.tsx:492:12" data-component-path="src/components/auth/SignUp.tsx" data-component-line="492" data-component-file="SignUp.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22className%22%3A%22p-0%20h-auto%20text-%5B%231954b3%5D%20hover%3Aunderline%20cursor-pointer%22%2C%22href%22%3A%22%2Fauth%2Flogin%22%2C%22textContent%22%3A%22Login%22%7D"
              className="p-0 h-auto text-[#1954b3] hover:underline cursor-pointer"
              href="/auth/login"
            >
              Login
            </Link>
          </div>
        </div>
      </div>

      {/* OTP Modal */}
      {showOtpModal && (
        <div data-component-id="src/components/auth/SignUp.tsx:504:8" data-component-path="src/components/auth/SignUp.tsx" data-component-line="504" data-component-file="SignUp.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22fixed%20inset-0%20bg-black%20bg-opacity-50%20flex%20items-center%20justify-center%20z-50%22%7D" className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div data-component-id="src/components/auth/SignUp.tsx:505:10" data-component-path="src/components/auth/SignUp.tsx" data-component-line="505" data-component-file="SignUp.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-white%20p-6%20rounded-lg%20max-w-md%20w-full%20mx-4%22%7D" className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
            <h2 data-component-id="src/components/auth/SignUp.tsx:506:12" data-component-path="src/components/auth/SignUp.tsx" data-component-line="506" data-component-file="SignUp.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-xl%20font-bold%20mb-4%22%2C%22textContent%22%3A%22Verify%20Your%20Email%22%7D" className="text-xl font-bold mb-4">Verify Your Email</h2>
            <p data-component-id="src/components/auth/SignUp.tsx:507:12" data-component-path="src/components/auth/SignUp.tsx" data-component-line="507" data-component-file="SignUp.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-gray-600%20mb-4%22%2C%22textContent%22%3A%22We've%20sent%20a%206-digit%20code%20to%22%7D" className="text-gray-600 mb-4">We've sent a 6-digit code to {storedEmail}</p>

            <div data-component-id="src/components/auth/SignUp.tsx:509:12" data-component-path="src/components/auth/SignUp.tsx" data-component-line="509" data-component-file="SignUp.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20gap-2%20mb-4%22%7D" className="flex gap-2 mb-4">
              {Array.from({ length: 6 }, (_, index) => (
                <Input data-component-id="src/components/auth/SignUp.tsx:511:16" data-component-path="src/components/auth/SignUp.tsx" data-component-line="511" data-component-file="SignUp.tsx" data-component-name="Input" data-component-content="%7B%22elementName%22%3A%22Input%22%2C%22type%22%3A%22text%22%2C%22className%22%3A%22w-12%20h-12%20text-center%20text-lg%20font-bold%20pr-%5B16px%5D%22%7D"
                  key={index}
                  ref={inputRefs[index]}
                  type="text"
                  maxLength={1}
                  value={otp[index] || ''}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(index, e)}
                  onPaste={handleOtpPaste}
                  className="w-12 h-12 text-center text-lg font-bold pr-[16px]"
                />
              ))}
            </div>

            <div data-component-id="src/components/auth/SignUp.tsx:525:12" data-component-path="src/components/auth/SignUp.tsx" data-component-line="525" data-component-file="SignUp.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20gap-2%20mb-4%22%7D" className="flex gap-2 mb-4">
              <Button data-component-id="src/components/auth/SignUp.tsx:526:14" data-component-path="src/components/auth/SignUp.tsx" data-component-line="526" data-component-file="SignUp.tsx" data-component-name="Button" data-component-content="%7B%22elementName%22%3A%22Button%22%2C%22className%22%3A%22flex-1%22%2C%22textContent%22%3A%22Verify%22%7D" onClick={handleVerifyOtp} disabled={otp.length !== 6} className="flex-1">
                Verify
              </Button>
              <Button data-component-id="src/components/auth/SignUp.tsx:529:14" data-component-path="src/components/auth/SignUp.tsx" data-component-line="529" data-component-file="SignUp.tsx" data-component-name="Button" data-component-content="%7B%22elementName%22%3A%22Button%22%2C%22className%22%3A%22flex-1%20%20text-%5B%23000000%5D%22%7D"
                onClick={handleResendOtp}
                disabled={isResendDisabled || isResending}
                variant="outline"
                className="flex-1  text-[#000000]"
              >
                {isResending ? (
                  <Loader2 data-component-id="src/components/auth/SignUp.tsx:536:18" data-component-path="src/components/auth/SignUp.tsx" data-component-line="536" data-component-file="SignUp.tsx" data-component-name="Loader2" data-component-content="%7B%22elementName%22%3A%22Loader2%22%2C%22className%22%3A%22w-4%20h-4%20animate-spin%22%7D" className="w-4 h-4 animate-spin" />
                ) : (
                  `Resend (${resendTimer}s)`
                )}
              </Button>
            </div>

            <Button data-component-id="src/components/auth/SignUp.tsx:543:12" data-component-path="src/components/auth/SignUp.tsx" data-component-line="543" data-component-file="SignUp.tsx" data-component-name="Button" data-component-content="%7B%22elementName%22%3A%22Button%22%2C%22className%22%3A%22w-full%20text-%5B%23000000%5D%22%2C%22textContent%22%3A%22Cancel%22%7D"
              onClick={() => setShowOtpModal(false)}
              variant="outline"
              className="w-full text-[#000000]"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
